import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Heading,
  Button,
  Flex,
} from "@chakra-ui/react";
import { FiArrowRight, FiActivity, FiBookOpen, FiLayers, FiUsers } from "react-icons/fi";
import ContactModal from "../../components/ContactModal";
const partnershipModels = [
  {
    number: "01",
    icon: FiBookOpen,
    title: "Research & Evidence",
    text:
      "Work with Serena on questions that require African health data, longitudinal evidence, biomarkers, genomics, preventive health research or translational science.",
  },
  {
    number: "02",
    icon: FiLayers,
    title: "Translation & Technology",
    text:
      "Move promising health evidence toward usable technologies, tools and products that can operate in real-world contexts.",
  },
  {
    number: "03",
    icon: FiActivity,
    title: "Clinical & Health-System Activation",
    text:
      "Design, test and measure the pathways required for a health innovation to move from technical possibility into working practice.",
  },
  {
    number: "04",
    icon: FiUsers,
    title: "Scientific Exchange",
    text:
      "Create opportunities for researchers, clinicians, institutions and innovators to share knowledge, develop relationships and identify areas for collaboration.",
  },
];

const partnerTypes = [
  "Research institutions",
  "Healthcare organisations",
  "Technology organisations",
  "Philanthropic and development funders",
  "Universities and academic networks",
  "Public-sector institutions",
];

function SectionLabel({ children }) {
  return (
    <Text
      fontSize="xs"
      fontWeight="700"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="#087F73"
      mb={4}
    >
      {children}
    </Text>
  );
}

function PartnerPage() {
  return (
    <Box>
      {/* HERO */}
      <Box
        bg="#F3F5F1"
        py={{ base: 20, md: 28, lg: 36 }}
        overflow="hidden"
      >
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Box maxW="900px">
            <SectionLabel>PARTNER WITH SERENA</SectionLabel>

            <Heading
              as="h1"
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
              lineHeight="1.05"
              letterSpacing="-0.045em"
              fontWeight="700"
              color="#17211F"
            >
              Help build the pathway from health evidence to action.
            </Heading>

            <Text
              mt={7}
              maxW="780px"
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.7"
              color="#4A5753"
            >
              Serena works with organisations that have evidence, capabilities,
              technologies, networks or resources that can contribute to
              better preventive and precision healthcare in Africa.
            </Text>
          </Box>
        </Container>
      </Box>

      {/* PARTNERSHIP THESIS */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 24 }}
            alignItems="start"
          >
            <Box>
              <SectionLabel>THE OPPORTUNITY</SectionLabel>

              <Heading
                as="h2"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
                maxW="620px"
              >
                Good ideas need more than funding or technology to become
                useful.
              </Heading>
            </Box>

            <Stack spacing={6} maxW="680px">
              <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                Moving health innovation into practice can require evidence,
                technical capability, clinical insight, institutional
                relationships and a pathway that can be tested in the real
                world.
              </Text>

              <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                Serena brings these elements together around defined health
                problems and practical opportunities for translation.
              </Text>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                lineHeight="1.7"
                fontWeight="600"
                color="#087F73"
              >
                We are interested in building the pathway, not simply
                describing the problem.
              </Text>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* PARTNERSHIP MODELS */}
      <Box bg="#F9FAF8" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SectionLabel>WAYS TO WORK TOGETHER</SectionLabel>

          <Heading
            as="h2"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "2xl", md: "4xl" }}
            lineHeight="1.15"
            letterSpacing="-0.035em"
            fontWeight="600"
            maxW="760px"
            mb={{ base: 12, md: 16 }}
          >
            Different capabilities. One translational pathway.
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {partnershipModels.map((item) => {
              const Icon = item.icon;

              return (
                <Box
                  key={item.number}
                  bg="white"
                  p={{ base: 7, md: 9 }}
                  minH={{ md: "270px" }}
                  border="1px solid"
                  borderColor="blackAlpha.100"
                >
                  <Flex
                    justify="space-between"
                    align="flex-start"
                    mb={8}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      letterSpacing="0.1em"
                      color="#087F73"
                    >
                      {item.number}
                    </Text>

                    <Icon size={20} />
                  </Flex>

                  <Heading
                    as="h3"
                    fontSize={{ base: "xl", md: "2xl" }}
                    letterSpacing="-0.025em"
                    mb={4}
                  >
                    {item.title}
                  </Heading>

                  <Text color="#596560" lineHeight="1.75">
                    {item.text}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>

      {/* WHO WE WORK WITH */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 24 }}
          >
            <Box>
              <SectionLabel>WHO WE WORK WITH</SectionLabel>

              <Heading
                as="h2"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
                maxW="620px"
              >
                Partnerships built around complementary capability.
              </Heading>
            </Box>

            <SimpleGrid
              columns={{ base: 1, sm: 2 }}
              spacingX={8}
              spacingY={5}
            >
              {partnerTypes.map((type) => (
                <Flex
                  key={type}
                  align="center"
                  gap={3}
                  py={3}
                  borderBottom="1px solid"
                  borderColor="blackAlpha.100"
                >
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    bg="#087F73"
                    flexShrink={0}
                  />

                  <Text fontSize={{ base: "sm", md: "md" }}>
                    {type}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </SimpleGrid>
        </Container>
      </Box>

      {/* PARTNERSHIP PRINCIPLE */}
      <Box bg="#17211F" color="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 24 }}
            alignItems="start"
          >
            <Box>
              <SectionLabel>OUR APPROACH</SectionLabel>

              <Heading
                as="h2"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
                maxW="620px"
              >
                Start with the problem. Build the pathway around it.
              </Heading>
            </Box>

            <Stack spacing={7}>
              {[
                {
                  label: "DEFINE",
                  text:
                    "Clarify the health problem, the evidence available and the outcome that matters.",
                },
                {
                  label: "ASSEMBLE",
                  text:
                    "Bring together the scientific, technical, clinical and institutional capabilities required.",
                },
                {
                  label: "TEST",
                  text:
                    "Build and test the pathway in a real-world context rather than assuming it will work.",
                },
                {
                  label: "MEASURE",
                  text:
                    "Generate evidence about what worked, what did not and what needs to change.",
                },
              ].map((step) => (
                <Box
                  key={step.label}
                  pb={6}
                  borderBottom="1px solid"
                  borderColor="whiteAlpha.200"
                >
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    letterSpacing="0.12em"
                    color="whiteAlpha.500"
                    mb={2}
                  >
                    {step.label}
                  </Text>

                  <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.7">
                    {step.text}
                  </Text>
                </Box>
              ))}
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Box
            borderTop="1px solid"
            borderColor="blackAlpha.100"
            pt={{ base: 10, md: 14 }}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              spacing={{ base: 8, md: 16 }}
              alignItems="end"
            >
              <Box maxW="720px">
                <SectionLabel>START A CONVERSATION</SectionLabel>

                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "4xl" }}
                  lineHeight="1.15"
                  letterSpacing="-0.035em"
                >
                  Have evidence, capability or a problem worth working on?
                </Heading>

                <Text
                  mt={5}
                  color="#596560"
                  fontSize={{ base: "md", md: "lg" }}
                  lineHeight="1.75"
                >
                  Tell us what you are trying to move from possibility to
                  practice.
                </Text>
              </Box>

              <Flex justify={{ base: "flex-start", md: "flex-end" }}>
                <ContactModal>
                  <Flex align="center" gap={2}>
                  Contact Serena
                  <FiArrowRight />
                  </Flex>
                </ContactModal>
              </Flex>
            </SimpleGrid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default PartnerPage;